import React, { useEffect, useMemo, useState } from "react";
import {
  CheckCircleFilled,
  ClockCircleOutlined,
  DownOutlined,
  EnvironmentOutlined,
  LeftOutlined,
  StarFilled,
  UpOutlined,
} from "@ant-design/icons";
import { FaBed, FaPlane } from "react-icons/fa";
import { HOTELSRIVER_OFFICIAL_LOGO_URL } from "../../utils/hotelsRiverBrandLogo";
import HpmHeroGallery from "../packageProfileMockup/HpmHeroGallery";
import HubwayzHotelAbout from "./HubwayzHotelAbout";
import {
  formatFx,
  formatIls,
} from "../packageProfileMockup/mockPackageProfileData";
import {
  buildFlightHotelDetailVm,
  canContinueFlightHotelRoomSelection,
  roomCardPhotos,
} from "./buildFlightHotelDetailVm";
import BookingStageNav from "./BookingStageNav";
import { useAppContext } from "../../contexts/AppContext";
import { hubwayzFxBlocksPayment } from "./hubwayz/resolveHubwayzHotelIlsPricing";
import {
  preferredHubwayzRoomId,
} from "./hubwayz/hubwayzSelectedOfferCurrency";
import { convertHubwayzSupplierAmountToDisplay } from "../hubwayzHotelsTab/hubwayzHotelLiveSearch";
import {
  formatHubwayzSaleDisplay,
  resolveHubwayzSearchCurrencyPrice,
} from "./hubwayz/hubwayzRoomSaleDisplay";
import { formatHotelStayHeader } from "./formatHotelStayHeader";
import { hubwayzRoomHeading } from "./hubwayzBeddingLabel";
import { useHubwayzHotelSaleCommission } from "./hubwayz/useHubwayzHotelSaleCommission";
import { useHubwayzVerifiedQuotes } from "./hubwayz/useHubwayzVerifiedQuotes";
import {
  hubwayzQuoteBlocksPayment,
  serverQuotePriceView,
} from "./hubwayz/hubwayzVerifiedQuote";
import { customerPriceWithAddedCommission } from "./hubwayz/hubwayzQuoteAddedCommission";
import UnifiedFlightHotelOrderSummary from "./UnifiedFlightHotelOrderSummary";
import HubwayzRoomCardFacts from "./hubwayz/HubwayzRoomCardFacts";
import HubwayzRoomPhotoSlider from "../hubwayzHotelPreview/HubwayzRoomPhotoSlider";
import HubwayzRoomAgentTools from "./hubwayz/HubwayzRoomAgentTools";
import { hubwayzRoomCardCopy } from "./hubwayz/hubwayzRoomCardCopy";
import { resolveUnifiedFlightLegs } from "./unifiedFlightLegs";
import {
  collectBoardFilterOptions,
  collectCancellationFilterOptions,
  collectRoomTypeFilterOptions,
  roomMatchesSelectionFilters,
} from "./roomSelectionFilters";
import "../packageProfileMockup/PackageProfileMockupPage.css";
import "./FlightHotelRoomSelectionPage.css";

function roomAdults(r) {
  const n = Number(r?.adults ?? r?.AdultsCount ?? r?.Adults);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

function roomChildrenCount(r) {
  const ages = r?.childAges ?? r?.KidsAges ?? r?.kidsAges ?? [];
  return Array.isArray(ages) ? ages.length : 0;
}

const kiwiLogo = (code) =>
  `https://images.kiwi.com/airlines/64/${encodeURIComponent(code)}.png`;

function StarRating({ stars }) {
  const n = Math.max(0, Math.min(5, Number(stars) || 0));
  return (
    <span className="hpm-stars" aria-label={`${n} כוכבים`}>
      {Array.from({ length: n }, (_, i) => (
        <StarFilled key={i} />
      ))}
    </span>
  );
}

function AirlineMark({ code, name }) {
  const [failed, setFailed] = useState(false);
  if (!code || failed) {
    return (
      <span className="hpm-airline-mark hpm-airline-mark--fallback" aria-hidden>
        <FaPlane />
      </span>
    );
  }
  return (
    <span className="hpm-airline-mark" title={name}>
      <img
        src={kiwiLogo(code)}
        alt=""
        width={28}
        height={28}
        loading="lazy"
        onError={() => setFailed(true)}
      />
    </span>
  );
}

function FlightLegRow({ leg, direction }) {
  if (!leg) return null;
  return (
    <div className="hpm-leg">
      <div className="hpm-leg__top">
        <AirlineMark code={leg.airlineCode} name={leg.airline} />
        <div className="hpm-leg__brand">
          <strong>{leg.airline}</strong>
          <span>{leg.flightNo}</span>
        </div>
        <span className="hpm-leg__dir">{direction}</span>
      </div>
      <div className="hpm-leg__times">
        <strong>{leg.depart}</strong>
        <span className="hpm-leg__route">
          {leg.from} → {leg.to}
        </span>
        <strong>{leg.arrive}</strong>
      </div>
      <div className="hpm-leg__meta">
        <span>{leg.duration}</span>
        {Number.isFinite(Number(leg.seatsLeft)) ? (
          <span>{leg.seatsLeft} מקומות פנויים</span>
        ) : null}
      </div>
    </div>
  );
}

function shownSupplierMoney(amount, supplierCurrency, displayCurrency, currencyRate) {
  const converted = convertHubwayzSupplierAmountToDisplay({
    amount,
    supplierCurrency,
    displayCurrency: displayCurrency || supplierCurrency,
    currencyRate,
  });
  if (!converted.ok) {
    return {
      amount: Number(amount),
      currency: supplierCurrency || displayCurrency || "",
    };
  }
  return { amount: converted.displayAmount, currency: converted.displayCurrency };
}

function formatShownMoney(amount, currency) {
  const code = String(currency || "").toUpperCase();
  if (code === "ILS") return formatIls(Math.round(Number(amount)));
  if (code === "USD" || code === "EUR") return formatFx(amount, code);
  if (!Number.isFinite(Number(amount))) return "";
  return `${Math.round(Number(amount))} ${code}`;
}

function formatRoomDelta(room, currency) {
  const fx = Number(room?.deltaPerPersonFx);
  if (Number.isFinite(fx) && fx > 0) {
    return { label: `+${formatFx(fx, currency)}`, sub: "לאדם מול חדר בסיס" };
  }
  const ils = Number(room?.deltaPerPersonIls);
  if (Number.isFinite(ils) && ils > 0) {
    return { label: `+${formatIls(ils)}`, sub: "לאדם מול חדר בסיס" };
  }
  return null;
}

function toggleFilterValue(current, value) {
  return current.includes(value)
    ? current.filter((item) => item !== value)
    : [...current, value];
}

function RoomFilterRow({ tone, label, options, selected, onToggle, onAll }) {
  const allActive = selected.length === 0;
  return (
    <div
      className={`usm-fh-room-filters__row hwz-filter hwz-filter--${tone}`}
      role="group"
      aria-label={label}
    >
      <span className="usm-fh-room-filters__label">
        <span className="hwz-filter__dot" aria-hidden />
        {label}
      </span>
      <button
        type="button"
        className={`usm-fh-room-filters__chip hwz-filter__all${allActive ? " is-active" : ""}`}
        aria-pressed={allActive}
        onClick={onAll}
      >
        הכל
      </button>
      <div className="hwz-filter__options">
        {options.map((option) => {
          const active = selected.includes(option.id);
          return (
            <button
              key={option.id}
              type="button"
              className={`usm-fh-room-filters__chip${active ? " is-active" : ""}`}
              aria-pressed={active}
              onClick={() => onToggle(option.id)}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function RoomOfferFilters({
  rooms,
  visibleCount,
  boards,
  cancellations,
  roomTypes,
  onChange,
}) {
  const boardOptions = useMemo(() => collectBoardFilterOptions(rooms), [rooms]);
  const cancelOptions = useMemo(
    () => collectCancellationFilterOptions(rooms),
    [rooms]
  );
  const typeOptions = useMemo(() => collectRoomTypeFilterOptions(rooms), [rooms]);
  const active = boards.length > 0 || cancellations.length > 0 || roomTypes.length > 0;
  if (boardOptions.length < 2 && cancelOptions.length < 2 && typeOptions.length < 2) {
    return null;
  }
  return (
    <div className="usm-fh-room-filters">
      {boardOptions.length > 1 ? (
        <RoomFilterRow
          tone="board"
          label="בסיס אירוח"
          options={boardOptions}
          selected={boards}
          onAll={() => onChange({ boards: [] })}
          onToggle={(value) => onChange({ boards: toggleFilterValue(boards, value) })}
        />
      ) : null}
      {cancelOptions.length > 1 ? (
        <RoomFilterRow
          tone="cancel"
          label="תנאי ביטול"
          options={cancelOptions}
          selected={cancellations}
          onAll={() => onChange({ cancellations: [] })}
          onToggle={(value) =>
            onChange({ cancellations: toggleFilterValue(cancellations, value) })
          }
        />
      ) : null}
      {typeOptions.length > 1 ? (
        <RoomFilterRow
          tone="type"
          label="סוג חדר"
          options={typeOptions}
          selected={roomTypes}
          onAll={() => onChange({ roomTypes: [] })}
          onToggle={(value) =>
            onChange({ roomTypes: toggleFilterValue(roomTypes, value) })
          }
        />
      ) : null}
      {active ? (
        <div className="usm-fh-room-filters__meta">
          <span>{`מציג ${visibleCount} מתוך ${rooms.length}`}</span>
          <button
            type="button"
            className="usm-fh-room-filters__clear"
            onClick={() =>
              onChange({ boards: [], cancellations: [], roomTypes: [] })
            }
          >
            ניקוי סינון
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Flight + hotel detail: live room offers and flights when liveContext is provided.
 */
export default function FlightHotelRoomSelectionPage({
  packageId,
  liveContext = null,
  onBack,
  onContinue,
}) {
  const {
    currencyRate,
    currencyRateSource,
    globalConstant,
    gcKey,
    getHrCommissionPercent,
    getHrCommissionPercentHotels,
    getAgentCommissionPercent,
    getCreditCardFeePercent,
  } = useAppContext();
  const hrCommissionHotelsSetting = globalConstant?.[gcKey.HR_COMMISSION_HOTELS];
  const agentSaleCommission = useHubwayzHotelSaleCommission();
  const offer = useMemo(
    () => buildFlightHotelDetailVm(packageId, liveContext),
    [packageId, liveContext]
  );

  const roomListKey = offer.rooms
    .map((room) => `${room.id}:${room.supplierCurrency || room.currency || ""}`)
    .join("|");
  const [flightId, setFlightId] = useState(offer.flightOptions[0]?.id);
  const [addedCommissionIls, setAddedCommissionIls] = useState(0);
  const [agentCommission, setAgentCommission] = useState(null);
  const [pickerCommission, setPickerCommission] = useState({ type: "+", value: 0 });
  const [roomId, setRoomId] = useState(() =>
    preferredHubwayzRoomId(offer.rooms, offer.displayCurrency)
  );
  const [flightPickerOpen, setFlightPickerOpen] = useState(false);
  const [toast, setToast] = useState("");
  const [boardFilter, setBoardFilter] = useState([]);
  const [cancelFilter, setCancelFilter] = useState([]);
  const [typeFilter, setTypeFilter] = useState([]);

  useEffect(() => {
    setFlightId(offer.flightOptions[0]?.id);
    setRoomId(preferredHubwayzRoomId(offer.rooms, offer.displayCurrency));
    setFlightPickerOpen(false);
    setBoardFilter([]);
    setCancelFilter([]);
    setTypeFilter([]);
  }, [offer.id, offer.displayCurrency, roomListKey]);

  const filteredRooms = useMemo(
    () =>
      offer.rooms.filter((room) =>
        roomMatchesSelectionFilters(room, {
          boards: boardFilter,
          cancellations: cancelFilter,
          roomTypes: typeFilter,
        })
      ),
    [offer.rooms, boardFilter, cancelFilter, typeFilter]
  );

  useEffect(() => {
    if (!filteredRooms.length) return;
    if (filteredRooms.some((room) => room.id === roomId)) return;
    setRoomId(filteredRooms[0].id);
  }, [filteredRooms, roomId]);

  const selectedFlight =
    offer.flightOptions.find((f) => f.id === flightId) ||
    offer.flightOptions[0];
  const selectedFlightLegs = useMemo(
    () =>
      resolveUnifiedFlightLegs({
        outbound: selectedFlight?.outbound,
        inbound: selectedFlight?.inbound,
        rawDeal: selectedFlight?.rawDeal,
      }),
    [selectedFlight]
  );
  const selectedRoom =
    filteredRooms.find((r) => r.id === roomId) || filteredRooms[0] || null;

  const hotelsOnly =
    offer.hotelsOnly === true ||
    offer.productType === "hotels_only" ||
    !offer.flightOptions?.length;

  const canContinue = canContinueFlightHotelRoomSelection({
    selectedRoom,
    selectedFlight,
    hotelsOnly,
    flightOptionsCount: offer.flightOptions?.length || 0,
  });

  const requestedRoomCount = Math.max(
    1,
    Number(offer.requestedRoomCount) ||
      (Array.isArray(liveContext?.appliedSearch?.rooms)
        ? liveContext.appliedSearch.rooms.length
        : 1)
  );

  const continueHint = !canContinue
    ? !selectedRoom
      ? hotelsOnly
        ? "בחרו חבילת חדרים כדי להמשיך"
        : "בחרו חדר וטיסה כדי להמשיך"
      : !hotelsOnly && !selectedFlight
        ? "בחרו חדר וטיסה כדי להמשיך"
        : selectedRoom?.isCompleteCombination === false
          ? `ההצעה אינה כוללת את כל ${requestedRoomCount} החדרים שביקשתם`
          : "בחרו הצעה מלאה כדי להמשיך"
    : null;

  const pricing = useMemo(() => {
    const pax = Math.max(1, Number(offer.paxCount) || 2);
    const hr = Number(offer.hrCommissionPerPerson) || 0;

    const hotelPer =
      Number.isFinite(Number(selectedRoom?.netPerPerson))
        ? Number(selectedRoom.netPerPerson)
        : Number.isFinite(Number(selectedRoom?.hotelNetTotal))
          ? Number(selectedRoom.hotelNetTotal) / pax
          : Number(offer.agentNetPerPerson) || 0;

    const flightPer = Number(selectedFlight?.pricePerPerson) || 0;
    const perFx = hotelPer + flightPer + hr;

    const perIlsRaw = Number(offer.agentNetPerPersonIls);
    const roomDeltaIls = Number(selectedRoom?.deltaPerPersonIls) || 0;
    const perIls =
      Number.isFinite(perIlsRaw) && perIlsRaw > 0
        ? perIlsRaw + roomDeltaIls
        : null;

    return {
      pax,
      perFx,
      perIls,
      totalFx: perFx * pax,
      totalIls: perIls != null ? perIls * pax : null,
      showIls: perIls != null && perIls > 0,
    };
  }, [offer, selectedFlight, selectedRoom]);

  const quoteClaims = useMemo(() => {
    if (!hotelsOnly || !liveContext?.searchId) return [];
    // Offer code + search id locate the server snapshot. Reconstructed
    // room name, bedding, board, currency, and occupancy are not sent:
    // a mismatch there rejects the quote and hides the server price.
    const selectedCode = selectedRoom?.hubwayzOfferCode || selectedRoom?.id || "";
    return (offer.rooms || [])
      .map((room) => {
        const offerCode = room.hubwayzOfferCode || room.id;
        const selected = offerCode === selectedCode && Number(pickerCommission.value) > 0;
        return {
          offerCode,
          searchId: liveContext.searchId,
          displayCurrency: offer.displayCurrency || null,
          ...(selected
            ? {
                commissionType: pickerCommission.type,
                commissionValue: pickerCommission.value,
              }
            : {}),
        };
      })
      .filter((row) => row.offerCode && row.searchId);
  }, [hotelsOnly, liveContext, offer.rooms, offer.displayCurrency, selectedRoom, pickerCommission]);
  const verifiedQuotes = useHubwayzVerifiedQuotes({
    enabled: hotelsOnly,
    claims: quoteClaims,
    refreshKey: `${agentSaleCommission?.type || "percentage"}:${agentSaleCommission?.value ?? 0}`,
  });
  const seededQuotes = useMemo(() => {
    const map = {};
    if (!hotelsOnly) return map;
    const rows = Array.isArray(liveContext?.verifiedQuotes) ? liveContext.verifiedQuotes : [];
    for (const row of rows) {
      const code = String(row?.offerCode || "");
      const view = serverQuotePriceView(row);
      if (code && view.agentPriceAmount) map[code] = view;
    }
    return map;
  }, [hotelsOnly, liveContext?.verifiedQuotes]);
  const quoteForRoom = (room) => {
    if (!room) return null;
    const code = room.hubwayzOfferCode || room.id;
    const fetched = verifiedQuotes.byCode[code];
    const seeded = seededQuotes[code];
    if (fetched?.agentPriceAmount) return fetched;
    if (seeded?.agentPriceAmount) return seeded;
    return fetched || seeded || null;
  };
  const selectedServerQuote = hotelsOnly ? quoteForRoom(selectedRoom) : null;

  const customerPrice = hotelsOnly
    ? selectedServerQuote || {
        source: "server_quote",
        ok: false,
        binding: false,
        status: verifiedQuotes.loading ? "loading" : "quote_unavailable",
        agentNetIls: null,
      }
    : null;
  const summaryCustomerPrice = agentCommission?.quote?.binding
    ? agentCommission.quote
    : customerPriceWithAddedCommission(customerPrice, addedCommissionIls);
  const quoteBlocked =
    hotelsOnly &&
    !customerPrice?.binding &&
    (verifiedQuotes.loading || hubwayzQuoteBlocksPayment(customerPrice));
  const fxBlocked = hotelsOnly ? false : hubwayzFxBlocksPayment(customerPrice);
  const displayCurrency = offer.displayCurrency || null;
  const roomSaleById = useMemo(() => {
    const map = new Map();
    if (!hotelsOnly) return map;
    for (const room of offer.rooms) {
      const quote = quoteForRoom(room);
      const shown = quote?.agentPriceAmount
        ? resolveHubwayzSearchCurrencyPrice({
            quote,
            displayCurrency: displayCurrency || quote.agentPriceCurrency,
            currencyRate,
            currencyRateSource,
          })
        : null;
      map.set(
        room.id,
        shown?.ok
          ? {
              ok: true,
              displayAmount: shown.displayAmount,
              displayCurrency: shown.displayCurrency,
              chargeIls: shown.chargeIls,
            }
          : { ok: false }
      );
    }
    return map;
  }, [
    hotelsOnly,
    offer.rooms,
    verifiedQuotes.byCode,
    seededQuotes,
    displayCurrency,
    currencyRate,
    currencyRateSource,
  ]);

  const showToast = (msg) => {
    setToast(msg);
    window.setTimeout(() => setToast(""), 2400);
  };

  const isLive = offer.source === "live";
  const currency = hotelsOnly
    ? customerPrice?.supplierCurrency || ""
    : selectedRoom?.currency || offer.currency || "USD";
  const bookingProduct = hotelsOnly ? "hotel" : "flightHotel";

  return (
    <div className="hpm-mock usm-fh-detail" dir="rtl" lang="he">
      <header className="hpm-topbar">
        <div className="hpm-topbar__brand">
          <img src={HOTELSRIVER_OFFICIAL_LOGO_URL} alt="HotelsRiver" />
          <span className="hpm-pill">
            {hotelsOnly ? "מלון" : "טיסה + מלון"}
            {isLive ? " · חי" : " · דמו"}
          </span>
        </div>
        <BookingStageNav
          product={bookingProduct}
          activeId="detail"
          onStage={(id) => {
            if (id === "results") onBack?.();
          }}
        />
        <button type="button" className="hpm-back" onClick={onBack}>
          <LeftOutlined />
          חזרה לתוצאות
        </button>
      </header>

      <div className="hpm-layout">
        <main className="hpm-main">
          <section className="hpm-hero" id="overview">
            <div className="hpm-hero__identity">
              <p className="hpm-hero__eyebrow">{offer.productLabel}</p>
              <div className="hpm-hero__title-row">
                <h1>{offer.hotelName}</h1>
                <StarRating stars={offer.stars} />
              </div>
              <p className="hpm-hero__loc">
                <EnvironmentOutlined />
                {offer.city}
                {offer.country ? `, ${offer.country}` : ""}
                {offer.address ? ` · ${offer.address}` : ""}
              </p>
              <div className="hpm-facts">
                {(() => {
                  const stay = formatHotelStayHeader({
                    checkIn:
                      liveContext?.appliedSearch?.checkIn || offer.checkInLabel,
                    checkOut:
                      liveContext?.appliedSearch?.checkOut || offer.checkOutLabel,
                    dateRangeLabel: offer.dateRangeLabel,
                    board: selectedRoom?.boardLabel || offer.boardLabel,
                  });
                  if (!stay.line) return null;
                  return (
                    <span className="hpm-facts__stay">
                      {stay.dates ? <bdi dir="ltr">{stay.dates}</bdi> : null}
                      {stay.dates && stay.boardLabel ? (
                        <span aria-hidden="true"> | </span>
                      ) : null}
                      {stay.boardLabel ? <span>{stay.boardLabel}</span> : null}
                    </span>
                  );
                })()}
                {offer.nights ? <span>{offer.nights} לילות</span> : null}
                <span
                  className={`hpm-alloc ${
                    offer.immediateConfirm ? "hpm-alloc--ok" : "hpm-alloc--wait"
                  }`}
                >
                  {offer.immediateConfirm ? (
                    <>
                      <CheckCircleFilled /> אישור מיידי
                    </>
                  ) : (
                    <>
                      <ClockCircleOutlined /> נדרש אישור מלון
                    </>
                  )}
                </span>
              </div>
              {offer.baggage?.bullets?.length ? (
                <p className="hpm-baggage">{offer.baggage.bullets.join(" · ")}</p>
              ) : null}
            </div>
            {offer.gallery?.length ? (
              <HpmHeroGallery
                images={offer.gallery}
                alt={offer.hotelName}
                lazyImages
              />
            ) : (
              <div className="usm-fh-gallery-fallback" aria-hidden>
                <img src={HOTELSRIVER_OFFICIAL_LOGO_URL} alt="" />
              </div>
            )}
          </section>

          <HubwayzHotelAbout description={offer.description} />

          {!hotelsOnly ? (
          <section className="hpm-section usm-fh-section" id="flights">
            <div className="hpm-section__head usm-fh-section__static-head">
              <div>
                <h2>פרטי טיסות</h2>
                <p>הטיסה שנבחרה מוצגת למטה · ניתן להחליף מדרופדאון</p>
              </div>
            </div>
            <div className="hpm-section__body">
              {offer.flightOptions.length && selectedFlight ? (
                <div className="usm-fh-flight-picker">
                  <div
                    className="hpm-flight-card is-selected usm-fh-flight-picker__selected"
                    aria-current="true"
                  >
                    <div className="hpm-flight-card__label">
                      {selectedFlight.label || "הטיסה שנבחרה"}
                      {Number(selectedFlight.pricePerPerson) > 0
                        ? ` · מ־${formatFx(
                            selectedFlight.pricePerPerson,
                            selectedFlight.currency || currency
                          )}`
                        : ""}
                    </div>
                    <div className="hpm-flight-card__legs">
                      <FlightLegRow
                        leg={selectedFlightLegs.outbound}
                        direction="הלוך"
                      />
                      <FlightLegRow
                        leg={selectedFlightLegs.inbound}
                        direction="חזור"
                      />
                    </div>
                  </div>

                  {offer.flightOptions.length > 1 ? (
                    <div className="usm-fh-flight-picker__swap">
                      <button
                        type="button"
                        className="usm-fh-flight-picker__toggle"
                        aria-expanded={flightPickerOpen}
                        aria-controls="usm-fh-flight-alts"
                        onClick={() => setFlightPickerOpen((o) => !o)}
                      >
                        <span>
                          {flightPickerOpen
                            ? "סגור אפשרויות טיסה"
                            : `החלף טיסה · ${offer.flightOptions.length - 1} אפשרויות נוספות`}
                        </span>
                        {flightPickerOpen ? <UpOutlined /> : <DownOutlined />}
                      </button>

                      {flightPickerOpen ? (
                        <div
                          id="usm-fh-flight-alts"
                          className="usm-fh-flight-picker__list"
                          role="listbox"
                          aria-label="החלפת טיסה"
                        >
                          {offer.flightOptions
                            .filter((opt) => opt.id !== selectedFlight.id)
                            .map((opt) => (
                              <button
                                type="button"
                                key={opt.id}
                                role="option"
                                className="hpm-flight-card usm-fh-flight-picker__option"
                                onClick={() => {
                                  setFlightId(opt.id);
                                  setFlightPickerOpen(false);
                                }}
                              >
                                <div className="hpm-flight-card__label">
                                  {opt.label}
                                  {Number(opt.pricePerPerson) > 0
                                    ? ` · מ־${formatFx(
                                        opt.pricePerPerson,
                                        opt.currency || currency
                                      )}`
                                    : ""}
                                </div>
                                <div className="hpm-flight-card__legs">
                                  <FlightLegRow
                                    leg={opt.outbound}
                                    direction="הלוך"
                                  />
                                  <FlightLegRow
                                    leg={opt.inbound}
                                    direction="חזור"
                                  />
                                </div>
                              </button>
                            ))}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ) : (
                <p className="usm-fh-empty">לא התקבלו טיסות LDS לחיפוש זה.</p>
              )}
            </div>
          </section>
          ) : null}

          <section
            className="hpm-section usm-fh-section is-emphasized"
            id="rooms"
          >
            <div className="hpm-section__head usm-fh-section__static-head">
              <div>
                <h2>
                  {requestedRoomCount > 1
                    ? "בחירת חבילת חדרים"
                    : "בחירת חדר"}
                </h2>
                {isLive && requestedRoomCount > 1 ? (
                  <p>{`כל שורה כוללת ${requestedRoomCount} חדרים`}</p>
                ) : !isLive ? (
                  <p>בחרו סוג חדר · דמו</p>
                ) : null}
              </div>
            </div>
            <div className="hpm-section__body">
              {offer.rooms.length ? (
                <>
                  <RoomOfferFilters
                    rooms={offer.rooms}
                    visibleCount={filteredRooms.length}
                    boards={boardFilter}
                    cancellations={cancelFilter}
                    roomTypes={typeFilter}
                    onChange={(patch) => {
                      if (Object.prototype.hasOwnProperty.call(patch, "boards")) {
                        setBoardFilter(patch.boards);
                      }
                      if (Object.prototype.hasOwnProperty.call(patch, "cancellations")) {
                        setCancelFilter(patch.cancellations);
                      }
                      if (Object.prototype.hasOwnProperty.call(patch, "roomTypes")) {
                        setTypeFilter(patch.roomTypes);
                      }
                    }}
                  />
                  {filteredRooms.length ? (
                <div
                  className="hpm-rooms"
                  role="radiogroup"
                  aria-label={
                    requestedRoomCount > 1
                      ? "בחירת חבילת חדרים"
                      : "בחירת חדר"
                  }
                >
                  {filteredRooms.map((room) => {
                    const selected = roomId === room.id;
                    const sale = hotelsOnly ? roomSaleById.get(room.id) : null;
                    const shown = hotelsOnly
                      ? sale?.ok
                        ? {
                            amount: sale.displayAmount,
                            currency: sale.displayCurrency,
                          }
                        : null
                      : shownSupplierMoney(
                          room.roomTotalFx,
                          room.supplierCurrency || room.currency || currency,
                          room.displayCurrency || offer.displayCurrency,
                          currencyRate
                        );
                    const delta = hotelsOnly
                      ? null
                      : formatRoomDelta(room, shown?.currency);
                    const combo = Array.isArray(room.combinationRooms)
                      ? room.combinationRooms
                      : [];
                    const isCombo = combo.length > 1;
                    const heading = hubwayzRoomHeading(
                      room.name,
                      !isCombo ? room.beddingLabel : ""
                    );
                    const cardCopy = hubwayzRoomCardCopy(room);
                    return (
                      <label
                        key={room.id}
                        className={`hpm-room usm-fh-combo-card${
                          selected ? " is-selected" : ""
                        }${isCombo ? " usm-fh-combo-card--multi" : ""}`}
                      >
                        <input
                          type="radio"
                          name="fh-room"
                          checked={selected}
                          onChange={() => setRoomId(room.id)}
                        />
                        <div className="usm-fh-combo-card__body hwz-room-layout">
                          <HubwayzRoomPhotoSlider
                            images={roomCardPhotos(room, offer.gallery)}
                            label={`תמונות ${heading.roomName}`}
                          />
                          <div className="hwz-room-layout__copy">
                          <div className="usm-fh-combo-card__header">
                            <div className="usm-fh-combo-card__title">
                              <FaBed aria-hidden />
                              <div
                                className="usm-fh-room-copy"
                                aria-label={heading.accessible || undefined}
                              >
                                <strong>{heading.roomName}</strong>
                                {heading.bedding ? (
                                  <span className="usm-fh-room-bed">
                                    {heading.bedding}
                                  </span>
                                ) : null}
                                <div className="usm-fh-combo-card__meta">
                                  <span
                                    className={`hpm-alloc ${
                                      room.immediateConfirm
                                        ? "hpm-alloc--ok"
                                        : "hpm-alloc--wait"
                                    }`}
                                  >
                                    {cardCopy.confirmLabel}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="usm-fh-combo-card__price">
                              {shown ? (
                                <strong className="usm-fh-combo-card__total">
                                  {hotelsOnly
                                    ? formatHubwayzSaleDisplay(
                                        shown.amount,
                                        shown.currency
                                      )
                                    : formatShownMoney(
                                        shown.amount,
                                        shown.currency
                                      )}
                                </strong>
                              ) : hotelsOnly ? (
                                <strong className="usm-fh-combo-card__total">
                                  —
                                </strong>
                              ) : room.roomTotalIls ? (
                                <strong className="usm-fh-combo-card__total">
                                  {formatIls(room.roomTotalIls)}
                                </strong>
                              ) : null}
                              <small>
                                {hotelsOnly
                                  ? "סה״כ נטו לסוכן"
                                  : isCombo
                                    ? "מחיר חבילה מלאה מהספק"
                                    : "מחיר חדר מהספק"}
                              </small>
                              {delta ? (
                                <small className="usm-fh-combo-card__delta">
                                  {delta.label} · {delta.sub}
                                </small>
                              ) : isCombo ? null : (
                                <small className="hpm-room__included">
                                  כלול במחיר
                                </small>
                              )}
                            </div>
                          </div>

                          <HubwayzRoomCardFacts room={room} />

                          {isCombo ? (
                            <div className="usm-fh-combo-rooms-grid">
                              {combo.map((cr) => {
                                const itemShown = Number.isFinite(
                                  Number(cr.itemPriceFx)
                                )
                                  ? shownSupplierMoney(
                                      cr.itemPriceFx,
                                      cr.itemCurrency ||
                                        room.supplierCurrency ||
                                        currency,
                                      room.displayCurrency ||
                                        offer.displayCurrency,
                                      currencyRate
                                    )
                                  : null;
                                const roomHeading = hubwayzRoomHeading(
                                  cr.name,
                                  cr.beddingLabel
                                );
                                return (
                                <div
                                  key={`${room.id}-${cr.roomIndex}`}
                                  className="usm-fh-combo-room-block"
                                >
                                  <div className="usm-fh-combo-room-block__h">
                                    חדר {cr.roomIndex}
                                  </div>
                                  <div className="usm-fh-room-copy">
                                    <div className="usm-fh-combo-room-block__name">
                                      {roomHeading.roomName}
                                    </div>
                                    {roomHeading.bedding ? (
                                      <div className="usm-fh-combo-room-block__bed">
                                        {roomHeading.bedding}
                                      </div>
                                    ) : null}
                                  </div>
                                  <ul className="usm-fh-combo-room-block__facts">
                                    <li>{cr.occupancyLabel}</li>
                                    {cr.boardLabel ? (
                                      <li>{cr.boardLabel}</li>
                                    ) : null}
                                    {cr.cancellationLabel ? (
                                      <li>{cr.cancellationLabel}</li>
                                    ) : null}
                                    {!hotelsOnly && itemShown ? (
                                      <li className="usm-fh-combo-room-block__sec-price">
                                        מחיר חדר (ספק):{" "}
                                        {formatShownMoney(
                                          itemShown.amount,
                                          itemShown.currency
                                        )}
                                      </li>
                                    ) : null}
                                  </ul>
                                </div>
                                );
                              })}
                            </div>
                          ) : null}

                          {selected && isCombo ? (
                            <div
                              className="usm-fh-combo-selected-banner"
                              role="status"
                            >
                              נבחר שילוב של {combo.length} חדרים
                            </div>
                          ) : isCombo ? (
                            <span className="usm-fh-room-note">
                              בחירה אחת מאשרת את כל {combo.length} החדרים
                            </span>
                          ) : null}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
                  ) : (
                    <p className="usm-fh-empty">אין הצעות שתואמות את הסינון.</p>
                  )}
                </>
              ) : (
                <p className="usm-fh-empty">
                  {requestedRoomCount > 1
                    ? `לא נמצאה חבילה מלאה ל־${requestedRoomCount} חדרים במלון זה. נסו מלון אחר או שנו את החיפוש.`
                    : "לא נמצאו חדרים למלון זה."}
                </p>
              )}
            </div>
          </section>
        </main>

        <aside className="hpm-summary" aria-label="סיכום ההזמנה">
          {selectedRoom ? (
            <HubwayzRoomAgentTools
              hotelId={offer.hotelId}
              hotelName={offer.hotelName}
              city={offer.city}
              country={offer.country}
              checkIn={
                liveContext?.appliedSearch?.checkIn || offer.checkInLabel
              }
              checkOut={
                liveContext?.appliedSearch?.checkOut || offer.checkOutLabel
              }
              roomName={selectedRoom.name}
              boardLabel={selectedRoom.boardLabel || offer.boardLabel}
              capacityLabel={selectedRoom.capacityLabel}
              quote={selectedServerQuote}
              displayCurrency={offer.displayCurrency || selectedServerQuote?.agentPriceCurrency || ""}
              cancellation={selectedRoom.cancellationSummary || ""}
              nights={offer.nights}
              gallery={Array.isArray(offer.gallery) ? offer.gallery : []}
              onToast={showToast}
              fallbackSearchId={liveContext?.searchId || ""}
              onCommissionChange={setPickerCommission}
              onCustomerQuote={(priced) => {
                if (priced?.commissionLocked && priced.binding) {
                  setAgentCommission({
                    type: priced.agentCommissionType,
                    value: priced.agentCommissionValue,
                    quote: priced,
                  });
                  setAddedCommissionIls(0);
                  return;
                }
                setAgentCommission(null);
                setAddedCommissionIls(priced?.ok ? priced.addedCommissionIls : 0);
              }}
            />
          ) : null}
          {hotelsOnly && customerPrice?.status === "price_changed" ? (
            <div className="usm-fh-price-change" role="status">
              <p>
                מחיר ההצעה עודכן. מחיר הסוכן הוא{" "}
                {formatHubwayzSaleDisplay(
                  resolveHubwayzSearchCurrencyPrice({
                    quote: customerPrice,
                    displayCurrency: displayCurrency || customerPrice.agentPriceCurrency,
                    currencyRate,
                    currencyRateSource,
                  }).displayAmount,
                  displayCurrency || customerPrice.agentPriceCurrency
                )}
                . מחיר הלקוח הוא {formatIls(customerPrice.customerSaleIls)}. הסכום לחיוב הוא{" "}
                {formatIls(customerPrice.chargeIls)}.
              </p>
              <button
                type="button"
                onClick={() => verifiedQuotes.acknowledge(customerPrice.offerCode)}
              >
                אישור המחיר המעודכן
              </button>
            </div>
          ) : null}
          {hotelsOnly && !verifiedQuotes.loading && customerPrice?.status !== "price_changed" && quoteBlocked ? (
            <p role="status">לא ניתן להמשיך בלי אימות מחיר מהשרת.</p>
          ) : null}
          <UnifiedFlightHotelOrderSummary
            hotelName={offer.hotelName}
            city={offer.city}
            country={offer.country}
            checkIn={
              liveContext?.appliedSearch?.checkIn || offer.checkInLabel
            }
            checkOut={
              liveContext?.appliedSearch?.checkOut || offer.checkOutLabel
            }
            dateRangeLabel={offer.dateRangeLabel}
            nights={offer.nights}
            roomsCount={requestedRoomCount}
            adultsCount={
              Array.isArray(liveContext?.appliedSearch?.rooms)
                ? liveContext.appliedSearch.rooms.reduce(
                    (s, r) => s + roomAdults(r),
                    0
                  )
                : pricing.pax
            }
            childrenCount={
              Array.isArray(liveContext?.appliedSearch?.rooms)
                ? liveContext.appliedSearch.rooms.reduce(
                    (s, r) => s + roomChildrenCount(r),
                    0
                  )
                : 0
            }
            roomName={selectedRoom?.name}
            boardLabel={selectedRoom?.boardLabel || offer.boardLabel}
            outbound={hotelsOnly ? null : selectedFlightLegs.outbound}
            inbound={hotelsOnly ? null : selectedFlightLegs.inbound}
            flightOptionLabel={hotelsOnly ? null : selectedFlight?.label}
            totalFx={pricing.totalFx}
            currency={currency}
            displayCurrency={offer.displayCurrency || null}
            customerPrice={summaryCustomerPrice}
            pricePresentation={hotelsOnly ? "offer" : "payment"}
            showTerms={false}
            primaryDisabled={!canContinue || fxBlocked || quoteBlocked}
            primaryDisabledHint={
              quoteBlocked
                ? customerPrice?.status === "price_changed"
                  ? "יש לאשר את המחיר המעודכן לפני המשך."
                  : verifiedQuotes.loading
                    ? "מחשב מחיר מאומת…"
                    : "לא ניתן להמשיך בלי אימות מחיר מהשרת."
                : fxBlocked
                ? "לא ניתן להמשיך לתשלום: מטבע הספק לא מוכר או שאין שער המרה תקין."
                : continueHint
            }
            primaryLabel={
              requestedRoomCount > 1
                ? "אישור חבילת חדרים והמשך"
                : "אישור חדר והמשך"
            }
            onPrimary={() => {
              if (!canContinue || fxBlocked || quoteBlocked) return;
              onContinue?.({
                packageId: offer.id,
                flightId: hotelsOnly ? null : flightId,
                roomId,
                hubwayzOfferCode: selectedRoom?.hubwayzOfferCode,
                hubwayzSearchId: liveContext?.searchId || null,
                hubwayzVerifiedQuote: agentCommission?.quote?.binding
                  ? agentCommission.quote
                  : customerPrice?.binding
                    ? summaryCustomerPrice
                    : null,
                agentCommissionType: agentCommission?.type || null,
                agentCommissionValue: agentCommission?.value ?? null,
                addedCommissionIls: agentCommission ? 0 : addedCommissionIls,
                hotelId: offer.hotelId,
                boardCode: selectedRoom?.boardCode || "",
                quoteRooms: liveContext?.appliedSearch?.rooms || [],
                supplierRoomName:
                  selectedRoom?.combinationRooms?.[0]?.name ||
                  (Number(selectedRoom?.roomsInOffer) > 1 ? "" : selectedRoom?.name || ""),
                quoteCurrency:
                  selectedRoom?.supplierNetCurrency ||
                  selectedRoom?.supplierCurrency ||
                  selectedRoom?.currency ||
                  "",
                hubwayzRoomItems: selectedRoom?.combinationRooms || [],
                roomsInOffer: selectedRoom?.roomsInOffer || 1,
                requestedRoomCount,
                searchRequest:
                  offer.hubwayzSearchRequest ||
                  liveContext?.searchRequest ||
                  null,
                source: offer.source,
                hotelsOnly,
                hotelName: offer.hotelName,
                city: offer.city,
                country: offer.country,
                dateRangeLabel: offer.dateRangeLabel,
                checkIn:
                  liveContext?.appliedSearch?.checkIn || offer.checkInLabel,
                checkOut:
                  liveContext?.appliedSearch?.checkOut || offer.checkOutLabel,
                nights: offer.nights,
                boardLabel: selectedRoom?.boardLabel || offer.boardLabel,
                roomName: selectedRoom?.name,
                beddingLabel: selectedRoom?.beddingLabel || "",
                cancellationSummary: selectedRoom?.cancellationSummary || "",
                stars: offer.stars || null,
                address: offer.address || "",
                paxCount: pricing.pax,
                displayCurrency: offer.displayCurrency || null,
                currency: hotelsOnly ? customerPrice?.supplierCurrency || "" : currency,
                supplierAmount: customerPrice?.ok ? customerPrice.supplierAmount : null,
                supplierCurrency: customerPrice?.ok
                  ? customerPrice.supplierCurrency
                  : hotelsOnly
                    ? ""
                    : currency,
                fxRate: customerPrice?.ok ? customerPrice.fxRate : null,
                ilsSalePrice: customerPrice?.ok ? customerPrice.ilsSalePrice : null,
                chargeIls: customerPrice?.ok ? customerPrice.chargeIls : null,
                hubwayzIlsPricing: customerPrice?.ok ? customerPrice : null,
                perFx: pricing.perFx,
                totalFx: pricing.totalFx,
                flightPricePerPerson: hotelsOnly
                  ? 0
                  : Number(selectedFlight?.pricePerPerson) || 0,
                hrCommissionPerPerson:
                  Number(offer.hrCommissionPerPerson) || 0,
                rawDeal: hotelsOnly ? null : selectedFlight?.rawDeal || null,
                outbound: hotelsOnly ? null : selectedFlightLegs.outbound,
                inbound: hotelsOnly ? null : selectedFlightLegs.inbound,
                flightOptionLabel: hotelsOnly
                  ? null
                  : selectedFlight?.label || null,
                flightLabel: hotelsOnly
                  ? null
                  : [
                      selectedFlightLegs.outbound?.airlineCode ||
                        selectedFlightLegs.outbound?.airline,
                      selectedFlightLegs.outbound?.flightNo,
                    ]
                      .filter(Boolean)
                      .join(" · "),
              });
            }}
            secondaryLabel="חזרה לתוצאות"
            onSecondary={onBack}
          />
        </aside>
      </div>

      {toast ? (
        <div className="hpm-toast" role="status">
          {toast}
        </div>
      ) : null}
    </div>
  );
}
